import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {Modal} from 'react-native';
import {WeeklyCardType} from '../vip.variable';
import {useTranslation} from 'react-i18next';
import {cn, toPriceStr} from '@/utils';
import ModalReceiveButton from '../components/modal-receive-button';
import {useScreenSize} from '@/common-pages/hooks/size.hooks';
import styles from '../vip.module.scss';
interface WeeklyCardReceivingProps {
  zIndex?: number;
}

export interface WeeklyCardReward {
  spin: number;
  spinCount: number;
  scratchCard: number;
  scratchCount: number;
  money: number;
  cardType: WeeklyCardType;
}
export interface WeeklyCardReceivingRef {
  show: (prizeList: WeeklyCardReward[]) => void;
}

const RewardCountItem = ({
  type,
  value,
  className,
}: {
  type: 'money' | 'spin' | 'scratchCard';
  value: number;
  className?: string;
}) => {
  const pic = useMemo(() => {
    const map = {
      money: require('@/assets/icons/vip/v2/money.webp'),
      spin: require('@/assets/icons/vip/v2/spin.webp'),
      scratchCard: require('@/assets/icons/vip/v2/scratch.webp'),
    };
    return map[type];
  }, [type]);
  return (
    <div className={cn('flex flex-row items-center gap-0.5', className)}>
      <div
        className="size-6 bg-full bg-no-repeat"
        style={{backgroundImage: `url(${pic})`}}
      />
      <span className="text-[var(--vip-modal-rewarditem-text)] text-base font-semibold">
        x
        {type === 'money'
          ? toPriceStr(value, {fixed: 0, suffixUnit: 'K', overPrice: 100000})
          : value}
      </span>
    </div>
  );
};

const RewardItem = ({
  weeklyCardReward,
  className,
}: {
  weeklyCardReward: WeeklyCardReward;
  className?: string;
}) => {
  const spin = useMemo(() => {
    return weeklyCardReward.spin * weeklyCardReward.spinCount;
  }, [weeklyCardReward.spin, weeklyCardReward.spinCount]);
  const scratchCard = useMemo(() => {
    return weeklyCardReward.scratchCard * weeklyCardReward.scratchCount;
  }, [weeklyCardReward.scratchCard, weeklyCardReward.scratchCount]);
  const cardImg = useMemo(() => {
    const map = {
      silver: require('@/assets/icons/vip/v2/silver-small.webp'),
      gold: require('@/assets/icons/vip/v2/gold-small.webp'),
      diamond: require('@/assets/icons/vip/v2/diamond-small.webp'),
    };
    return map[weeklyCardReward.cardType];
  }, [weeklyCardReward.cardType]);

  const borderClass = useMemo(() => {
    const map = {
      silver: 'border-[#115A72]',
      gold: 'border-[#9C5B19]',
      diamond: 'border-[#A71F80]',
    };
    return map[weeklyCardReward.cardType];
  }, [weeklyCardReward.cardType]);
  const {t} = useTranslation();
  return (
    <div
      className={cn(
        'flex flex-col p-2.5 gap-1.5 bg-[#FEFCD2] rounded-lg border border-[#E3BD47] shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]',
        className,
      )}>
      <div className="flex flex-row gap-1 items-center">
        <span className="size-1.5 rounded-full bg-[#9C4B19]" />
        <div
          className={cn(
            'w-6 h-4 bg-no-repeat bg-full border rounded-sm',
            borderClass,
          )}
          style={{
            backgroundImage: `url(${cardImg})`,
          }}
        />
        <span className="text-[#9C4B19] text-sm font-normal">
          {t(`vip.label.${weeklyCardReward.cardType}WeekCard`)}
        </span>
      </div>
      <div className="flex flex-row items-center px-2.5">
        <RewardCountItem
          className="flex-1"
          type="money"
          value={weeklyCardReward.money}
        />
        <RewardCountItem className="flex-1" type="spin" value={spin} />
        <RewardCountItem
          className="flex-1"
          type="scratchCard"
          value={scratchCard}
        />
      </div>
    </div>
  );
};

const WeeklyCardReceiving = forwardRef<
  WeeklyCardReceivingRef,
  WeeklyCardReceivingProps
>(({zIndex}, ref) => {
  const [visible, setVisible] = useState(false);
  const modelRef = useRef<Modal>(null);
  const [prizeList, setPrizeList] = useState<WeeklyCardReward[]>([]);

  useImperativeHandle(ref, () => ({
    show: (_prizeList: WeeklyCardReward[]) => {
      setVisible(true);
      setPrizeList(_prizeList);
    },
  }));

  useEffect(() => {
    if (visible && zIndex != null && modelRef.current) {
      let parent = modelRef.current as unknown as HTMLDivElement;
      while (parent && parent.parentElement !== document.body) {
        parent = parent.parentElement as HTMLDivElement;
      }
      (parent.children[0] as HTMLDivElement).style.zIndex = zIndex + '';
    }
  }, [visible, zIndex]);

  const {screenHeight} = useScreenSize();

  const needCompressHeight = useMemo(() => {
    return screenHeight < 700 && prizeList.length >= 2;
  }, [screenHeight, prizeList]);

  const {t} = useTranslation();
  return (
    <Modal
      visible={visible}
      ref={modelRef}
      transparent
      statusBarTranslucent={false}
      animationType="fade">
      <div
        className={'w-full h-full bg-black/50'}
        onClick={() => setVisible(false)}>
        <div
          className={cn(
            'absolute left-1/2 -translate-x-1/2 w-[23.4375rem] h-[50.75rem] bg-no-repeat bg-full flex flex-col items-center',
            visible
              ? styles['animate-base-modal-show']
              : styles['animate-base-modal-hide'],
            !needCompressHeight ? 'top-0' : '-top-10',
          )}
          onClick={e => e.stopPropagation()}
          style={{
            backgroundImage: `url(${require('@/assets/imgs/vip/v2/week-card-rewards.webp')})`,
          }}>
          <div
            className={cn(
              'flex flex-col w-[19.4375rem] py-[1.875rem] mt-[15.875rem] px-5 bg-gradient-to-b from-[#FFF7E1] to-white rounded-xl shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] border-2 border-white',
              !needCompressHeight ? 'gap-6' : 'gap-3 py-3',
            )}>
            <div className="flex flex-col items-center">
              <h3 className="text-[#A93813] text-base font-bold">
                {t('vip.tip.weekCardReward.title')}
              </h3>
              <p className="text-[0.625rem] text-[#9C4B19] leading-[normal] mt-3">
                {t('vip.tip.weekCardReward.p1')}
              </p>
              <p className="text-[0.625rem] text-[#C82222] leading-[normal]">
                {t('vip.tip.weekCardReward.p2')}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {prizeList.map(item => (
                <RewardItem key={item.cardType} weeklyCardReward={item} />
              ))}
            </div>
            <ModalReceiveButton
              className="mx-auto"
              onClick={() => setVisible(false)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
});

export default WeeklyCardReceiving;
